import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useStorage } from '../hooks/Utils/useStorage';

type TutorialContextType = {
  tutorialDone: boolean | undefined; // puede ser undefined mientras carga
  setTutorialDone: (value: boolean) => void;
  startTour: boolean;
  setStartTour: (value: boolean) => void;
  goToStep: number;
  setGoToStep: (value: number) => void;
};

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const [tutorialDone, setTutorialDoneState] = useState<boolean | undefined>(undefined);
  const [startTour, setStartTour] = useState<boolean>(false);
  const [goToStep, setGoToStep] = useState(0);

  const { setValue, getValue } = useStorage();

  // Leer desde storage al montar
  useEffect(() => {
    const loadTutorialStatus = async () => {
      const value = await getValue('TOUR_GENERAL_DONE');

      setTutorialDoneState(value !== null ? JSON.parse(value) : false);
    };
    loadTutorialStatus();
  }, [getValue]);

  const setTutorialDone = useCallback(
    (value: boolean) => {
      setTutorialDoneState(value);
      setValue('TOUR_GENERAL_DONE', value.toString());
    },
    [setValue],
  );

  const contextValue = useMemo(
    () => ({
      tutorialDone,
      setTutorialDone,
      startTour,
      setStartTour,
      goToStep,
      setGoToStep,
    }),
    [
      goToStep,
      setTutorialDone,
      startTour,
      tutorialDone,
    ],
  );

  return (
    <TutorialContext.Provider value={contextValue}>{children}</TutorialContext.Provider>
  );
};

export const useTutorial = (): TutorialContextType => {
  const context = useContext(TutorialContext);
  if (!context) throw new Error('useTutorial debe usarse dentro de TutorialProvider');
  return context;
};
